import { availableSeats as computeAvailableSeats, initialRegistrationType } from "./session_capacity.js";

/**
 * Initialize the trainee registration Alpine component.
 * This should be called before Alpine.start().
 *
 * @param {import("alpinejs").Alpine} Alpine - The Alpine instance
 */
export function initCompanyTraineeRegistration(Alpine) {
  Alpine.data("companyTraineeRegistration", () => ({
    trainees: [{}],
    selectedSessionId: "",
    registrationType: null,
    traineeLabel: "",
    lastnameLabel: "",
    firstnameLabel: "",
    emailLabel: "",
    civilityLabel: "",
    civilityOptions: [],
    programCapacity: null,

    init() {
      this.traineeLabel = this.$el.dataset.traineeLabel;
      this.lastnameLabel = this.$el.dataset.lastnameLabel;
      this.firstnameLabel = this.$el.dataset.firstnameLabel;
      this.emailLabel = this.$el.dataset.emailLabel;
      this.civilityLabel = this.$el.dataset.civilityLabel;
      this.spotsLabel = this.$el.dataset.spotsLabel;
      this.civilityOptions = JSON.parse(this.$el.dataset.civilityOptions);
      this.registrationType = initialRegistrationType(this.$el);
      this.programCapacity = this.$el.dataset.programCapacity ? parseInt(this.$el.dataset.programCapacity) : null;

      // Restore pre-selected session from URL param, mirroring choose_date x-init logic
      const sessionId = new URLSearchParams(window.location.search).get("session_id");
      if (sessionId) {
        this.selectedSessionId = sessionId;
      }

      document.addEventListener("session-changed", (e) => {
        this.selectedSessionId = e.detail.sessionId;
      });

      document.addEventListener("registrationTypeChanged", (e) => {
        this.registrationType = e.detail.type;
      });
    },

    // Same rule as the server: the trainee rows win, the count input is the fallback once the rows
    // are gone. The input's own edits are announced by `live_price.js`, which renders without rows too.
    get learnerCount() {
      if (this.trainees.length > 0) {
        return this.trainees.length;
      }

      const input = document.querySelector(".js-learner-registration-count");

      return Math.max(1, parseInt(input?.value) || 1);
    },

    notifyLearnerCount() {
      document.dispatchEvent(new CustomEvent("learner-count-changed", { detail: { count: this.learnerCount } }));
    },

    get availableSeats() {
      return computeAvailableSeats(this.$el, {
        registrationType: this.registrationType,
        selectedSessionId: this.selectedSessionId,
        programCapacity: this.programCapacity,
      });
    },

    get remainingSeats() {
      const availableSeats = this.availableSeats;
      if (availableSeats === null) {
        return null;
      }

      return availableSeats - this.trainees.length;
    },

    get canAddTrainee() {
      const availableSeats = this.availableSeats;
      if (availableSeats === null) {
        return true;
      }

      return this.trainees.length < availableSeats;
    },

    addTrainee() {
      if (this.canAddTrainee) {
        this.trainees.push({ lastname: "", firstname: "", email: "", civility: "other" });
        // hide input and make it optional
        const input = document.querySelector(".js-learner-registration-count");
        input?.closest("label").classList.add("hidden");
        input?.removeAttribute("required");
        document.dispatchEvent(new CustomEvent("learner-count-visibility-changed"));
        this.notifyLearnerCount();
      }
    },

    removeTrainee(index) {
      this.trainees.splice(index, 1);
      if (this.trainees.length === 0) {
        // show input and make it required if initially required
        const input = document.querySelector(".js-learner-registration-count");
        input?.closest("label").classList.remove("hidden");
        // Presence answers it, not the value: an optional field renders no `data-required` at all.
        if (input?.hasAttribute("data-required")) input.setAttribute("required", "");
        document.dispatchEvent(new CustomEvent("learner-count-visibility-changed"));
      }
      this.notifyLearnerCount();
    },
  }));
}
